import React, { createContext, useState, useEffect, useContext } from 'react';
import type { TreatmentPlan, TreatmentStep } from '../types/treatmentPlan';
import { TreatmentPlanApi, StepApi, FolderApi } from '../utils/ApiService';

interface FolderStructure {
  id: string | number;
  name: string;
  type: 'folder' | 'treatment';
  children?: FolderStructure[];
}

interface TreatmentPlanContextType {
  treatmentPlans: TreatmentPlan[];
  folders: FolderStructure | null;
  loading: boolean;
  error: string | null;
  fetchTreatmentPlans: (folderId?: string) => Promise<void>;
  fetchFolders: () => Promise<void>;
  getPatientTreatmentPlans: (patientId: string) => Promise<TreatmentPlan[]>;
  getTreatmentPlan: (id: string) => Promise<TreatmentPlan | null>;
  createTreatmentPlan: (plan: Partial<TreatmentPlan>) => Promise<TreatmentPlan | null>;
  updateTreatmentPlan: (id: string, data: Partial<TreatmentPlan>) => Promise<TreatmentPlan | null>;
  createFolder: (name: string, parentId?: string) => Promise<FolderStructure | null>;
  updateFolder: (id: string, name: string) => Promise<boolean>;
  deleteFolder: (id: string) => Promise<boolean>;
  updateTreatmentStep: (planId: string, steps: TreatmentStep[]) => Promise<TreatmentPlan | null>;
  createTreatmentStep: (
    planId: string,
    step: Partial<TreatmentStep>
  ) => Promise<TreatmentStep | null>;
  updateSingleStep: (
    planId: string,
    stepId: string,
    step: Partial<TreatmentStep>
  ) => Promise<TreatmentStep | null>;
  deleteTreatmentStep: (planId: string, stepId: string) => Promise<boolean>;
  resetTreatmentPlan: () => void;
}

const TreatmentPlanContext = createContext<TreatmentPlanContextType | undefined>(undefined);

export const TreatmentPlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);
  const [folders, setFolders] = useState<FolderStructure | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all treatment plans from the folder structure
  const fetchTreatmentPlans = async (folderId?: string) => {
    setLoading(true);
    setError(null);

    try {
      // With the recursive structure, we need to extract treatment plans from the folder tree
      if (folders) {
        // First, find the current folder in the tree
        let currentFolder = folders;

        if (folderId) {
          // Find the specific folder in the tree
          currentFolder = FolderApi.findFolderInTree(folders, folderId);
        }

        if (currentFolder && currentFolder.children) {
          // Extract treatment plans from the current folder
          const treatments = currentFolder.children
            .filter(item => item.type === 'treatment')
            .map(item => ({
              id: item.id.toString(),
              name: item.name,
              folder: folderId || '',
              description: '',
              dateCreated: new Date().toISOString().split('T')[0],
              dateModified: new Date().toISOString().split('T')[0],
              status: 'active' as const,
              steps: '[]',
              completedSteps: 0,
              totalSteps: 0,
            }));

          setTreatmentPlans(treatments);
        } else {
          setTreatmentPlans([]);
        }
      } else {
        // If we don't have the folder structure yet, fetch it first
        await fetchFolders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error processing treatment plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the full folder structure
  const fetchFolders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await FolderApi.getAll();

      if (response.success && response.data) {
        setFolders(response.data);
      } else {
        setError(response.error || 'Failed to fetch folders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get treatment plans for a specific patient
  const getPatientTreatmentPlans = async (patientId: string): Promise<TreatmentPlan[]> => {
    try {
      const response = await TreatmentPlanApi.getByPatient(patientId);

      if (response.success && response.data) {
        return response.data as TreatmentPlan[];
      } else {
        setError(response.error || 'Failed to fetch patient treatment plans');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error fetching patient treatment plans:', err);
      return [];
    }
  };

  // Get a treatment plan by ID
  const getTreatmentPlan = async (id: string): Promise<TreatmentPlan | null> => {
    try {
      const response = await TreatmentPlanApi.getById(id);

      if (response.success && response.data) {
        return response.data as TreatmentPlan;
      } else {
        setError(response.error || 'Failed to fetch treatment plan');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error fetching treatment plan:', err);
      return null;
    }
  };

  // Create a new treatment plan
  const createTreatmentPlan = async (
    plan: Partial<TreatmentPlan>
  ): Promise<TreatmentPlan | null> => {
    try {
      // Ensure required properties are set with defaults if not provided
      const planToCreate: Omit<TreatmentPlan, 'id'> = {
        name: plan.name || 'New Treatment Plan',
        patientId: plan.patientId || '',
        folder: plan.folder || '',
        steps: plan.steps || JSON.stringify([]),
        dateCreated: plan.dateCreated || new Date().toISOString().split('T')[0],
        dateModified: plan.dateModified || new Date().toISOString().split('T')[0],
        status: plan.status || 'active',
        completedSteps: plan.completedSteps || 0,
        totalSteps: plan.totalSteps || 0,
        notes: plan.notes || '',
        createdBy: plan.createdBy || '',
        description: plan.description || '',
      };

      const response = await TreatmentPlanApi.create(planToCreate);

      if (response.success && response.data?.id) {
        // Fetch the newly created plan to get complete details
        const newPlanResponse = await getTreatmentPlan(response.data.id);
        if (newPlanResponse) {
          setTreatmentPlans(prev => [...prev, newPlanResponse]);
          return newPlanResponse;
        }
      }

      setError(response.error || 'Failed to create treatment plan');
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error creating treatment plan:', err);
      return null;
    }
  };

  // Update an existing treatment plan
  const updateTreatmentPlan = async (
    id: string,
    data: Partial<TreatmentPlan>
  ): Promise<TreatmentPlan | null> => {
    try {
      const response = await TreatmentPlanApi.update(id, data);

      if (response.success) {
        // Fetch the updated plan to get the latest data
        const updatedPlan = await getTreatmentPlan(id);

        if (updatedPlan) {
          setTreatmentPlans(prev => prev.map(p => (p.id === id ? updatedPlan : p)));
          return updatedPlan;
        }
      } else {
        setError(response.error || 'Failed to update treatment plan');
      }

      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error updating treatment plan:', err);
      return null;
    }
  };

  // Create a new folder
  const createFolder = async (name: string, parentId?: string): Promise<FolderStructure | null> => {
    try {
      const response = await FolderApi.create(name, parentId);

      if (response.success && response.data) {
        // Refresh the folder structure
        await fetchFolders();
        return response.data;
      } else {
        setError(response.error || 'Failed to create folder');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error creating folder:', err);
      return null;
    }
  };

  // Update a folder
  const updateFolder = async (id: string, name: string): Promise<boolean> => {
    try {
      const response = await FolderApi.update(id, name);

      if (response.success) {
        // Refresh the folder structure
        await fetchFolders();
        return true;
      } else {
        setError(response.error || 'Failed to update folder');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error updating folder:', err);
      return false;
    }
  };

  // Delete a folder
  const deleteFolder = async (id: string): Promise<boolean> => {
    try {
      const response = await FolderApi.delete(id);

      if (response.success) {
        // Refresh the folder structure
        await fetchFolders();
        return true;
      } else {
        setError(response.error || 'Failed to delete folder');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error deleting folder:', err);
      return false;
    }
  };

  // Update steps in a treatment plan
  const updateTreatmentStep = async (
    planId: string,
    steps: TreatmentStep[]
  ): Promise<TreatmentPlan | null> => {
    try {
      const currentPlan = await getTreatmentPlan(planId);

      if (!currentPlan) return null;

      // Count completed steps
      const completedSteps = steps.filter(step => step.completed).length;

      // Update the treatment plan with new steps and progress
      return updateTreatmentPlan(planId, {
        steps: JSON.stringify(steps),
        completedSteps,
        totalSteps: steps.length,
        dateModified: new Date().toISOString().split('T')[0],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error updating treatment step:', err);
      return null;
    }
  };

  // Create a new step
  const createTreatmentStep = async (
    planId: string,
    step: Partial<TreatmentStep>
  ): Promise<TreatmentStep | null> => {
    try {
      const response = await StepApi.create(planId, {
        title: step.title || 'New Step',
        description: step.description || 'placeholder',
        startDate: step.startDate || new Date().toISOString().split('T')[0],
        endDate: step.endDate,
        completed: step.completed || false,
        stepNum: step.stepNum || 0,
        note: step.note || '',
        folder: step.folder,
        audioId: step.audioId,
        mediaId: step.mediaId,
      });

      if (response.success && response.data) {
        // Update the treatment plan to include the new step
        await getTreatmentPlan(planId);
        return response.data;
      } else {
        setError(response.error || 'Failed to create step');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error creating step:', err);
      return null;
    }
  };

  // Update a single step
  const updateSingleStep = async (
    planId: string,
    stepId: string,
    step: Partial<TreatmentStep>
  ): Promise<TreatmentStep | null> => {
    try {
      const response = await StepApi.update(planId, stepId, {
        title: step.title,
        description: step.description,
        startDate: step.startDate,
        endDate: step.endDate,
        completed: step.completed,
        stepNum: step.stepNum,
        note: step.note,
        folder: step.folder,
        audioId: step.audioId,
        mediaId: step.mediaId,
      });

      if (response.success && response.data) {
        // Update the treatment plan to include the updated step
        await getTreatmentPlan(planId);
        return response.data;
      } else {
        setError(response.error || 'Failed to update step');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error updating step:', err);
      return null;
    }
  };

  // Delete a step
  const deleteTreatmentStep = async (planId: string, stepId: string): Promise<boolean> => {
    try {
      const response = await StepApi.delete(planId, stepId);

      if (response.success) {
        // Update the treatment plan to reflect the deleted step
        await getTreatmentPlan(planId);
        return true;
      } else {
        setError(response.error || 'Failed to delete step');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Error deleting step:', err);
      return false;
    }
  };

  const resetTreatmentPlan = () => {
    setTreatmentPlans([]);
  };

  // Initial load of treatment plans and folders
  useEffect(() => {
    fetchTreatmentPlans();
    fetchFolders();
  }, []);

  return (
    <TreatmentPlanContext.Provider
      value={{
        treatmentPlans,
        folders,
        loading,
        error,
        fetchTreatmentPlans,
        fetchFolders,
        getPatientTreatmentPlans,
        getTreatmentPlan,
        createTreatmentPlan,
        updateTreatmentPlan,
        createFolder,
        updateFolder,
        deleteFolder,
        updateTreatmentStep,
        createTreatmentStep,
        updateSingleStep,
        deleteTreatmentStep,
        resetTreatmentPlan,
      }}
    >
      {children}
    </TreatmentPlanContext.Provider>
  );
};

export const useTreatmentPlans = () => {
  const context = useContext(TreatmentPlanContext);
  if (context === undefined) {
    throw new Error('useTreatmentPlans must be used within a TreatmentPlanProvider');
  }
  return context;
};
